<div class="modal-container">
  <mat-toolbar class="modal-header">
    <div class="header-content">
      <h1 class="modal-title">
        @if (userFullName) {
          {{ "generic.loaded_evidences_of" | translate }} <span>{{ userFullName }}</span>
        } @else {
          {{ "components.decision-table.new-decision-table.compile-proposal.loaded-evidences" | translate }}
        }
      </h1>
      <button mat-icon-button class="close-btn" (click)="closeDialogue()" [matTooltip]="'generic.buttons.close' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </mat-toolbar>

  <div class="modal-body">
    @if (manageDisplay && attachments.length > 0) {
      <div class="alert alert-info d-flex align-items-start small mb-3" role="note">
        <mat-icon class="mr-2 flex-shrink-0">info</mat-icon>
        <span>{{ 'modals.manage-attachments.intro' | translate }}</span>
      </div>
      <div cdkDropList (cdkDropListDropped)="dropAttachment($event)">
        @for (attachment of attachments; track attachment; let i = $index) {
          <mat-card cdkDrag appearance="outlined" class="mb-2"
            [class.border-warning]="attachment.is_primary">
            <div class="border rounded bg-light mb-2" style="min-height: 72px" *cdkDragPlaceholder></div>
            <mat-card-content class="d-flex flex-wrap align-items-center py-2">
              <div class="d-flex align-items-center mr-2">
                <button mat-icon-button type="button" cdkDragHandle class="text-muted" style="cursor: grab"
                  [matTooltip]="'modals.manage-attachments.drag' | translate">
                  <mat-icon>drag_indicator</mat-icon>
                </button>
                <span class="font-weight-bold text-primary mr-1">{{ i + 1 }}</span>
                <button mat-icon-button type="button" (click)="togglePrimary(attachment)"
                  [class.text-warning]="attachment.is_primary" [class.text-muted]="!attachment.is_primary"
                  [matTooltip]="(attachment.is_primary ? 'modals.manage-attachments.unset-primary' : 'modals.manage-attachments.set-primary') | translate">
                  <mat-icon>{{ attachment.is_primary ? 'star' : 'star_border' }}</mat-icon>
                </button>
                <mat-icon color="primary" class="mx-1">{{ preview.icon(attachment) }}</mat-icon>
              </div>

              <div class="d-flex flex-wrap align-items-center flex-grow-1" style="min-width: 260px">
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="flex-grow-1 mr-2 my-1">
                  <mat-label>{{ 'modals.manage-attachments.document' | translate }}</mat-label>
                  <input matInput [(ngModel)]="attachment.title" (ngModelChange)="markDisplayDirty()" maxlength="255">
                </mat-form-field>
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="flex-grow-1 mr-2 my-1">
                  <mat-label>{{ 'modals.manage-attachments.reference' | translate }}</mat-label>
                  <input matInput [(ngModel)]="attachment.reference" (ngModelChange)="markDisplayDirty()" maxlength="500"
                    [placeholder]="'modals.manage-attachments.reference-placeholder' | translate">
                </mat-form-field>
                @if (preview.kind(attachment) === 'pdf') {
                  <mat-form-field appearance="outline" subscriptSizing="dynamic" class="mr-2 my-1" style="width: 96px">
                    <mat-label>{{ 'modals.manage-attachments.page' | translate }}</mat-label>
                    <input matInput type="number" min="1" [(ngModel)]="attachment.page" (ngModelChange)="markDisplayDirty()">
                  </mat-form-field>
                }
                @if (attachment.type === 'FILE') {
                  <mat-slide-toggle color="primary" class="my-1 mr-2" [checked]="!!attachment.is_read_only"
                    (change)="toggleReadOnly(attachment)" [matTooltip]="'modals.manage-attachments.read-only-hint' | translate">
                    {{ 'modals.manage-attachments.read-only' | translate }}
                  </mat-slide-toggle>
                }
              </div>

              <div class="d-flex align-items-center ml-auto">
                <button mat-icon-button type="button" color="primary" (click)="openInViewer(attachment)"
                  [disabled]="attachment.type === 'FILE' && !canPersistDisplay" [matTooltip]="'generic.view' | translate">
                  <mat-icon>{{ attachment.type === 'FILE' ? 'visibility' : 'open_in_new' }}</mat-icon>
                </button>
                <button mat-icon-button type="button" color="warn" (click)="deleteAttachment(i)" [matTooltip]="'generic.remove' | translate">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </mat-card-content>
          </mat-card>
        }
      </div>
    } @else if (attachments.length > 0) {
      <div class="table-card">
        <table mat-table [dataSource]="attachments">

          <!-- Name Column -->
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>{{ "generic.title" | translate }}</th>
            <td mat-cell *matCellDef="let element">
              <div class="title-cell">
                <mat-icon class="file-icon" [class.pubmed]="element.type === 'PUBMED'" [class.link]="element.type === 'LINK'">
                  {{ element.type === 'PUBMED' ? 'science' : (element.type === 'LINK' ? 'link' : 'insert_drive_file') }}
                </mat-icon>
                <a [href]="element.link" target="_blank" class="document-link">{{element.title}}</a>
              </div>
            </td>
          </ng-container>

          <!-- Type Column -->
          <ng-container matColumnDef="type">
            <th mat-header-cell *matHeaderCellDef>{{ "generic.type" | translate }}</th>
            <td mat-cell *matCellDef="let element">
              <span class="type-badge" [class.badge-pubmed]="element.type === 'PUBMED'" [class.badge-file]="element.type === 'FILE'" [class.badge-link]="element.type === 'LINK'">
                {{element.type}}
              </span>
            </td>
          </ng-container>

          <!-- Date Column -->
          <ng-container matColumnDef="loaded_at">
            <th mat-header-cell *matHeaderCellDef>{{ "generic.upload_date" | translate }}</th>
            <td mat-cell *matCellDef="let element" class="date-cell">
              {{ element.created_at | formatdate | date : 'short'}}
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let element; let i = index" class="actions-cell">
              <div class="d-flex align-items-center gap-1">
                <a [href]="element.link" target="_blank" mat-icon-button color="primary" [matTooltip]="'generic.view' | translate">
                  <mat-icon>open_in_new</mat-icon>
                </a>
                @if (editable) {
                  <button mat-icon-button color="warn" (click)="deleteAttachment(i)" [matTooltip]="'generic.remove' | translate">
                    <mat-icon>delete</mat-icon>
                  </button>
                }
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns; let i = index" class="evidence-row"></tr>
        </table>
      </div>
    } @else {
      <div class="empty-state">
        <mat-icon>folder_off</mat-icon>
        <p>{{ "generic.no-attachments-uploaded" | translate }}</p>
      </div>
    }
  </div>

  @if (editable) {
    <div class="modal-footer d-flex justify-content-end align-items-center gap-2 p-3">
      @if (manageDisplay && attachments.length > 0) {
        <span class="small text-muted mr-auto">
          @if (!canPersistDisplay) {
            {{ 'modals.manage-attachments.saved-with-survey' | translate }}
          } @else if (displayDirty) {
            {{ 'modals.manage-attachments.unsaved' | translate }}
          }
        </span>
        @if (canPersistDisplay) {
          <button mat-stroked-button color="primary" type="button" [disabled]="!displayDirty || savingDisplay" (click)="saveDisplay()">
            <mat-icon class="mr-1">save</mat-icon>
            {{ 'modals.manage-attachments.save' | translate }}
          </button>
        }
      }
      <button mat-raised-button color="primary" class="d-flex align-items-center" [matMenuTriggerFor]="menuAttachment">
        <mat-icon class="mr-2">add</mat-icon>
        {{ 'generic.add' | translate }}
      </button>
      <mat-menu #menuAttachment="matMenu">
        <button mat-menu-item (click)="onClickAddNewEvidence('PUBMED')">
          <mat-icon aria-hidden="false" class="attachment-evidence-icon">science</mat-icon>
          Pubmed
        </button>
        <button mat-menu-item (click)="onClickAddNewEvidence('FILE')">
          <mat-icon aria-hidden="false" class="attachment-evidence-icon">insert_drive_file</mat-icon>
          File
        </button>
        <button mat-menu-item (click)="onClickAddNewEvidence('LINK')">
          <mat-icon aria-hidden="false" class="attachment-evidence-icon">link</mat-icon>
          Link
        </button>
      </mat-menu>
    </div>
  }
</div>
